import type { CategoryType } from '@models/legal-directive';

const ACCEPT_HEROIC_MEASURES = 'accept-all';
const ALL_ORGAN_TISSUES = 'all-organs-and-tissue';
const ALL_BLOOD_PRODUCTS = 'all-blood-products';
const consentToAllMap: Record<CategoryType, string> = {
  'heroic-measures': ACCEPT_HEROIC_MEASURES,
  'specific-organs': ALL_ORGAN_TISSUES,
  'blood-products': ALL_BLOOD_PRODUCTS,
};

const REFUSE_HEROIC_MEASURES = 'none-i-refuse-all';
const NO_ORGAN_TISSUES = 'no-organs-and-tissue';
const NO_BLOOD_PRODUCTS = 'no-blood-products';
const consentToNoneMap: Record<CategoryType, string> = {
  'heroic-measures': REFUSE_HEROIC_MEASURES,
  'specific-organs': NO_ORGAN_TISSUES,
  'blood-products': NO_BLOOD_PRODUCTS,
};

const displayNameMap: Record<CategoryType, string> = {
  'heroic-measures': 'Heroic Measures',
  'specific-organs': 'Organ Donations',
  'blood-products': 'Blood Products',
};

const heroicMeasuresUrlMap: Record<string, string> = {
  [ACCEPT_HEROIC_MEASURES]: 'I Accept All Heroic Measures',
  [REFUSE_HEROIC_MEASURES]: 'None, I Refuse All Heroic Measures',
  'cpr-chest-compressions': 'CPR/Chest Compressions',
  'defibrillation-cardiac-resuscitation':
    'Defibrillation/Cardiac Resuscitation',
  'mechanical-ventilation-respiration':
    'Mechanical Ventilation (Mechanical Respiration)',
  'inotrope-support': 'Inotrope Support',
  'dialysis-of-any-kind': 'Dialysis of any kind',
  'tube-feeding': 'Tube Feeding',
  antibiotics: 'Antibiotics',
  'icu-admission': 'ICU Admission',
};

const organDonationUrlMap: Record<string, string> = {
  [ALL_ORGAN_TISSUES]: 'All Organs and Tissue',
  [NO_ORGAN_TISSUES]: 'No Organs and Tissue',
  kidneys: 'Kidneys',
  heart: 'Heart',
  eyes: 'Eyes',
  bone: 'Bone',
  liver: 'Liver',
  lung: 'Lung',
  skin: 'Skin',
  pancreas: 'Pancreas',
};

const bloodProductsUrlMap: Record<string, string> = {
  [ALL_BLOOD_PRODUCTS]: 'All Blood Products',
  [NO_BLOOD_PRODUCTS]: 'No Blood Products',
  'whole-blood-packed-rbc-red-blood-cell-concentrate':
    'Whole Blood, Packed RBC (Red Blood Cell Concentrate)',
  'washed-rbc-concentrate': 'Washed RBC Concentrate',
  'frozen-rbc-concentrate': 'Frozen RBC Concentrate',
  platelets: 'Platelets',
  'white-blood-cell-depleted': 'White Blood Cell Depleted',
  'filtered-packed-rbcs': 'Filtered Packed RBCs',
  'whole-ffp-fresh-frozen-plasma': 'Whole FFP (Fresh Frozen Plasma)',
  cryoprecipitate: 'Cryoprecipitate',
  'factor-viii-concentrate': 'Factor VIII Concentrate',
  'protein-s-and-protein-c': 'Protein S and Protein C',
  'factor-ix-concentrate': 'Factor IX Concentrate',
  'prothombin-complex-concentrate': 'Prothombin Complex Concentrate',
  albumin: 'Albumin',
  immunoglobulins: 'Immunoglobulins',
  autotransfusion: 'Autotransfusion',
};

const getLegalDirMap = (type: CategoryType) =>
  // eslint-disable-next-line no-nested-ternary
  type === 'heroic-measures'
    ? heroicMeasuresUrlMap
    : type === 'blood-products'
      ? bloodProductsUrlMap
      : organDonationUrlMap;

/**
 * Determines if 'ALL' or 'NONE' options are selected using the identifier and type
 * @param identifier Legal direction option in form
 * @param type Type of legal directive (heroic measures, blood products, organs/tissues)
 * @returns T if all/none option is selected, F if option has not been selected
 */
function allOrNoneSelected(identifier: string, type: CategoryType) {
  switch (type) {
    case 'blood-products':
      switch (identifier) {
        case ALL_BLOOD_PRODUCTS:
        case NO_BLOOD_PRODUCTS:
          return true;
        default:
          return false;
      }
    case 'heroic-measures':
      switch (identifier) {
        case ACCEPT_HEROIC_MEASURES:
        case REFUSE_HEROIC_MEASURES:
          return true;
        default:
          return false;
      }
    case 'specific-organs':
      switch (identifier) {
        case ALL_ORGAN_TISSUES:
        case NO_ORGAN_TISSUES:
          return true;
        default:
          return false;
      }
  }
}

export {
  allOrNoneSelected,
  bloodProductsUrlMap,
  consentToAllMap,
  consentToNoneMap,
  displayNameMap,
  getLegalDirMap,
  heroicMeasuresUrlMap,
  organDonationUrlMap,
};
